/**
 * バックギャモン盤に重ねる矢印 (パズルの答え表示・チュートリアル用)。
 *
 * 盤の DOM (.bg-point[data-point-abs] / .bg-bar-top / .bg-off[data-off-abs]) の位置を
 * 描画後に測って SVG を重ねる。 盤のレイアウトに座標を持ち込まないので、 盤の見た目を
 * 変えても矢印はついてくる。 起点は動かす駒 (山の一番上)、 終点は着地する段。 同じ点に
 * 複数の駒が出入りするときは、 矢印の順に山の高さを増減させて段をずらす。
 */

import { type RefObject, useLayoutEffect, useState } from 'react';
import type { BgArrow, BgState, Side } from '../../games/backgammon';

interface Seg {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  tone: NonNullable<BgArrow['tone']>;
  hit: boolean;
}

/** 1 列に見せる駒の最大数 (BgPointView の visible と同じ)。 */
const MAX_VISIBLE = 5;

export function BgArrowLayer({
  caseRef,
  state,
  arrows,
  yourSide,
}: {
  caseRef: RefObject<HTMLDivElement>;
  state: BgState;
  arrows: BgArrow[];
  yourSide: Side;
}) {
  const [segs, setSegs] = useState<Seg[]>([]);
  const [box, setBox] = useState<{ w: number; h: number; cs: number }>({ w: 0, h: 0, cs: 0 });

  useLayoutEffect(() => {
    const root = caseRef.current;
    if (!root) return;
    const measure = () => {
      const base = root.getBoundingClientRect();
      const checker = root.querySelector('.bg-checker') as HTMLElement | null;
      const anyPoint = root.querySelector('.bg-point') as HTMLElement | null;
      const cs = checker?.getBoundingClientRect().height || anyPoint?.getBoundingClientRect().width || 20;
      // 矢印の順に山の高さを動かす (自分の駒 = 正、 相手の駒 = 負)
      const count = new Map<number, number>();
      state.points.forEach((p, i) => {
        count.set(i + 1, p.owner == null ? 0 : p.owner === yourSide ? p.count : -p.count);
      });
      const anchor = (at: number | 'bar' | 'off', leaving: boolean): { x: number; y: number } | null => {
        let el: HTMLElement | null;
        if (at === 'bar') el = root.querySelector('.bg-bar-top');
        else if (at === 'off') el = root.querySelector('[data-off-abs]');
        else el = root.querySelector(`[data-point-abs="${at}"]`);
        if (!el) return null;
        if (at === 'bar') {
          // バーは駒の山の一番外側 (最後の子) から出す
          const cs = el.querySelectorAll('.bg-checker');
          const last = cs[cs.length - 1] as HTMLElement | undefined;
          if (last) el = last;
        }
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2 - base.left;
        if (typeof at !== 'number') return { x: cx, y: r.top + r.height / 2 - base.top };
        const n = Math.max(0, count.get(at) ?? 0);
        const slot = Math.min(leaving ? n - 1 : n, MAX_VISIBLE - 1);
        const top = el.classList.contains('top');
        const y = top ? r.top + (slot + 0.5) * cs : r.bottom - (slot + 0.5) * cs;
        return { x: cx, y: y - base.top };
      };
      const out: Seg[] = [];
      for (const a of arrows) {
        const p1 = anchor(a.from, true);
        if (typeof a.from === 'number') count.set(a.from, (count.get(a.from) ?? 0) - 1);
        if (typeof a.to === 'number' && (count.get(a.to) ?? 0) < 0) count.set(a.to, 0); // ヒット
        const p2 = anchor(a.to, false);
        if (typeof a.to === 'number') count.set(a.to, (count.get(a.to) ?? 0) + 1);
        if (p1 && p2) out.push({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, tone: a.tone ?? 'plain', hit: !!a.hit });
      }
      setSegs(out);
      setBox({ w: base.width, h: base.height, cs });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [caseRef, state, arrows, yourSide]);

  if (segs.length === 0 || box.w === 0) return null;
  const sw = Math.max(2, box.cs * 0.14);
  const curves = layoutCurves(segs, box);
  return (
    <svg className="bg-arrows" width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`} aria-hidden>
      {curves.map((c, i) => {
        const s = segs[i];
        // 先端: 終点での接線 (終点 - 制御点) の向きに三角形。 線は三角形の根元で止める
        const tx = s.x2 - c.mx;
        const ty = s.y2 - c.my;
        const tl = Math.hypot(tx, ty) || 1;
        const ux = tx / tl;
        const uy = ty / tl;
        const hl = sw * 2.6;
        const hw = sw * 1.9;
        const bx = s.x2 - ux * hl;
        const by = s.y2 - uy * hl;
        const head = `M${s.x2},${s.y2} L${bx - uy * hw},${by + ux * hw} L${bx + uy * hw},${by - ux * hw} Z`;
        const d = `M${s.x1},${s.y1} Q${c.mx},${c.my} ${bx},${by}`;
        return (
          <g key={i} className={`bg-arrow bg-arrow--${s.tone}`}>
            {/* 端の丸みは線端 (round cap) と先端の角 (round join) で出す。 点や輪は描かない */}
            <path className="bg-arrow-halo" d={d} strokeWidth={sw + 3} />
            <path className="bg-arrow-halo bg-arrow-halo--head" d={head} strokeWidth={sw * 0.6 + 3} />
            <path className="bg-arrow-line" d={d} strokeWidth={sw} />
            <path className="bg-arrow-head" d={head} strokeWidth={sw * 0.6} />
          </g>
        );
      })}
    </svg>
  );
}

interface Curve {
  mx: number;
  my: number;
}

/** 2 次ベジエを折れ線にして返す (交差判定用)。 */
function sample(s: Seg, c: Curve, n = 16): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const a = (1 - t) * (1 - t);
    const b = 2 * (1 - t) * t;
    const d = t * t;
    out.push([a * s.x1 + b * c.mx + d * s.x2, a * s.y1 + b * c.my + d * s.y2]);
  }
  return out;
}

function segCross(p: [number, number], q: [number, number], r: [number, number], s: [number, number]): boolean {
  const o = (a: [number, number], b: [number, number], c: [number, number]) =>
    Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
  return o(p, q, r) * o(p, q, s) < 0 && o(r, s, p) * o(r, s, q) < 0;
}

function crossings(a: [number, number][], b: [number, number][]): number {
  let n = 0;
  for (let i = 0; i + 1 < a.length; i++) {
    for (let j = 0; j + 1 < b.length; j++) {
      if (segCross(a[i], a[i + 1], b[j], b[j + 1])) n++;
    }
  }
  return n;
}

/**
 * 各矢印の曲がり (制御点) を決める。 候補は盤の内側 (縦の中央) へ膨らませる大小と、 外側へ
 * 膨らませる大小。 矢印が 4 本まで (出目 1 回分はこれに収まる) なら全組み合わせを試し、
 * 交差の数が最小の組を選ぶ。 同数なら候補の先頭 (内側へ控えめ) を優先する。 先に置いた
 * 矢印を固定して後ろだけ曲げると、 1 本目が相手の山の上を通るときに避けられない。
 */
function layoutCurves(segs: Seg[], box: { h: number; cs: number }): Curve[] {
  const options: Curve[][] = segs.map((s) => {
    const dx = s.x2 - s.x1;
    const dy = s.y2 - s.y1;
    const len = Math.hypot(dx, dy) || 1;
    let nx = -dy / len;
    let ny = dx / len;
    const cx = (s.x1 + s.x2) / 2;
    const cy = (s.y1 + s.y2) / 2;
    if (Math.abs(cy + ny - box.h / 2) > Math.abs(cy - ny - box.h / 2)) {
      nx = -nx;
      ny = -ny;
    }
    const base = Math.min(len * 0.2, box.cs * 2.2);
    return [base, base * 1.9, -base * 0.6, base * 0.4, base * 2.8, -base * 1.1].map((b) => ({
      mx: cx + nx * b,
      my: cy + ny * b,
    }));
  });
  const pts = segs.map((s, i) => options[i].map((o) => sample(s, o)));
  // 組 (i の候補 a, j の候補 b) の交差数を先に数えておく
  const cost = (i: number, a: number, j: number, b: number) => crossings(pts[i][a], pts[j][b]);
  const n = segs.length;
  if (n > 4) {
    // 多いときは前から貪欲に置く
    const pick: number[] = [];
    for (let i = 0; i < n; i++) {
      let best = 0;
      let bestC = Number.POSITIVE_INFINITY;
      for (let a = 0; a < options[i].length; a++) {
        const c = pick.reduce((acc, b, j) => acc + cost(i, a, j, b), 0);
        if (c < bestC) {
          best = a;
          bestC = c;
        }
      }
      pick.push(best);
    }
    return pick.map((a, i) => options[i][a]);
  }
  let bestPick: number[] = new Array(n).fill(0);
  let bestScore = Number.POSITIVE_INFINITY;
  const cur: number[] = [];
  const rec = (i: number) => {
    if (i === n) {
      let c = 0;
      for (let x = 0; x < n; x++) for (let y = x + 1; y < n; y++) c += cost(x, cur[x], y, cur[y]);
      // 交差を最優先、 同数なら候補の順 (控えめな曲げ) を優先
      const score = c * 1000 + cur.reduce((s, a) => s + a, 0);
      if (score < bestScore) {
        bestScore = score;
        bestPick = [...cur];
      }
      return;
    }
    for (let a = 0; a < options[i].length; a++) {
      cur[i] = a;
      rec(i + 1);
    }
  };
  rec(0);
  return bestPick.map((a, i) => options[i][a]);
}
